import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, map, of, TimeoutError, timeout } from 'rxjs';
import { DashboardApiService } from './dashboardApi.service';
import { DashboardViewModel, MaintenanceItemViewModel } from './dashboardModel';
import { VehicleProfilesService } from '../vehicle-profiles/vehicle-profiles.service';
import { VehicleProfile } from '../vehicle-profiles/vehicle-profiles.model';
import { MaintenancePart, MaintenanceSchedulingService } from '../maintenance/maintenance-scheduling.service';

interface MaintenanceDisplayRule {
  keywords: string[];
  label: string;
  icon: string;
  useCompactIcon?: boolean;
}

const defaultMaintenanceIcon = 'assets/dashboard/iconCabinFilter.svg';
const maintenanceDisplayRules: MaintenanceDisplayRule[] = [
  { keywords: ['oil'], label: 'Engine Oil', icon: 'assets/dashboard/iconOil.svg' },
  { keywords: ['spark'], label: 'Spark Plugs', icon: 'assets/dashboard/iconSpark.svg', useCompactIcon: true },
  { keywords: ['brake'], label: 'Brake Pads', icon: 'assets/dashboard/iconBrake.svg' },
  { keywords: ['cabin'], label: 'Cabin Air Filter', icon: 'assets/dashboard/iconCabinFilter.svg' },
  { keywords: ['engine'], label: 'Engine Air Filter', icon: 'assets/dashboard/iconEngineFilter.svg' }
];
// Keep dashboard cards in a fixed order regardless of backend ordering.
const dashboardMaintenancePartNames = [
  'Engine Oil',
  'Spark Plugs',
  'Brake Pads',
  'Cabin Air Filter',
  'Engine Air Filter'
] as const;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboardView.html',
  styleUrls: ['./dashboardView.css']
})
export class DashboardComponent implements OnInit {
  private readonly requestTimeoutMs = 18000;
  private readonly seedRequestTimeoutMs = 12000;
  private readonly mileageUpdateTimeoutMs = 35000;
  private readonly mileageReconcileDelayMs = 1500;
  private readonly mileageReconcileMaxAttempts = 30;
  private readonly mileageRefreshRetryMaxAttempts = 4;
  private loadWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private mileageReconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private activeRequestKey = '';
  private loadSequence = 0;
  model: DashboardViewModel | null = null;
  loading = false;
  errorMessage = '';
  isMechanicView = false;
  mechanicName = '';
  mileageInput = '';
  mileageSaving = false;
  mileageRefreshing = false;
  mileageErrorMessage = '';
  mileageSuccessMessage = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly dashboardApi: DashboardApiService,
    private readonly vehicleProfilesApi: VehicleProfilesService,
    private readonly maintenanceSchedulingService: MaintenanceSchedulingService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.destroyRef.onDestroy(() => {
      this.clearLoadWatchdog();
      this.clearMileageReconciliation();
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.clearLegacyDashboardCache();
        const roleFromQuery = (params.get('role') ?? '').trim().toLowerCase();
        const roleFromStorage = this.getSavedRole();
        this.isMechanicView =
          roleFromQuery === 'mechanic' || (roleFromQuery.length === 0 && roleFromStorage === 'mechanic');

        const mechanicFromQuery = (params.get('mechanic') ?? '').trim();
        this.mechanicName = mechanicFromQuery || (this.isMechanicView ? this.getSavedUsername() ?? '' : '');

        const ownerFromQuery = (params.get('ownerId') ?? '').trim();
        const ownerId = ownerFromQuery || this.getSavedOwnerId() || '';
        const vehicleIdRaw = params.get('vehicleId');
        const vehicleIdParsed = vehicleIdRaw ? Number.parseInt(vehicleIdRaw, 10) : Number.NaN;
        const savedVehicleId = this.getSavedVehicleId();
        const vehicleId = Number.isFinite(vehicleIdParsed) ? vehicleIdParsed : savedVehicleId;
        if (vehicleId !== undefined) {
          this.persistSelectedVehicleId(vehicleId);
        }
        if (ownerId) {
          this.persistOwnerId(ownerId);
        }

        if (!ownerId && !this.isMechanicView) {
          this.model = null;
          this.loading = false;
          this.errorMessage = 'Owner account is required.';
          return;
        }

        this.loadDashboard(ownerId, vehicleId, true);
      });
  }

  // Dashboard loading strategy:
  // 1) show cached/shell quickly,
  // 2) seed from profile + part-state APIs,
  // 3) apply canonical dashboard response when ready.
  loadDashboard(ownerId = '', vehicleId?: number, forceProfileSeed = false, refreshToken?: number): void {
    const normalizedOwnerId = ownerId.trim();
    if (!normalizedOwnerId && !this.isMechanicView) {
      this.model = null;
      this.loading = false;
      this.errorMessage = 'Owner account is required.';
      return;
    }

    const requestKey = this.createRequestKey(normalizedOwnerId, vehicleId);
    const currentLoadSequence = ++this.loadSequence;
    const isSameSelection =
      this.model !== null &&
      this.createRequestKey(this.model.ownerId, this.model.vehicleId) === requestKey;
    const cachedModel =
      this.readCachedDashboard(normalizedOwnerId, vehicleId) ??
      this.readCachedDashboardByOwner(normalizedOwnerId, vehicleId);

    this.activeRequestKey = requestKey;

    if (!isSameSelection) {
      this.model = cachedModel ?? this.createDashboardShell(normalizedOwnerId, vehicleId);
      this.syncMileageInput(this.model);
    }

    this.loading = true;
    this.errorMessage = '';
    this.clearLoadWatchdog();

    this.loadWatchdogTimer = setTimeout(() => {
      if (!this.loading || this.loadSequence !== currentLoadSequence) {
        return;
      }

      if (!this.model) {
        this.model = this.createDashboardShell(normalizedOwnerId, vehicleId);
        this.syncMileageInput(this.model);
      }
      this.loading = false;
      this.errorMessage = 'Live dashboard data is slow. Showing available profile data.';
    }, this.requestTimeoutMs + 1000);

    if (normalizedOwnerId && (forceProfileSeed || this.model === null || this.isDashboardShell(this.model))) {
      this.seedDashboardFromProfile(
        normalizedOwnerId,
        vehicleId,
        requestKey,
        cachedModel,
        forceProfileSeed,
        currentLoadSequence,
        refreshToken
      );
    }

    const request$ = this.dashboardApi.getDashboard(normalizedOwnerId, vehicleId, refreshToken);
    const guardedRequest$ = request$.pipe(
      timeout(this.requestTimeoutMs),
      catchError(() => of(null))
    );

    guardedRequest$
      .pipe(
        map((response) => {
          if (!this.isDashboardResponseValid(response)) {
            return null;
          }

          return response;
        }),
        finalize(() => {
          this.clearLoadWatchdog();
          if (this.loadSequence === currentLoadSequence) {
            this.loading = false;
          }
        })
      )
      .subscribe({
        next: (response) => {
          if (this.activeRequestKey !== requestKey || this.loadSequence !== currentLoadSequence) {
            return;
          }

          if (!response) {
            this.errorMessage = 'Live dashboard data is slow. Showing available profile data.';
            return;
          }

          this.model = response;
          this.syncMileageInput(response);
          this.persistDashboardCache(response);
          this.errorMessage = '';
        },
        error: () => {
          if (this.activeRequestKey !== requestKey || this.loadSequence !== currentLoadSequence) {
            return;
          }

          this.errorMessage = 'Live dashboard data is slow. Showing available profile data.';
        }
      });
  }

  private isDashboardResponseValid(response: DashboardViewModel | null | undefined): response is DashboardViewModel {
    if (!response) {
      return false;
    }

    return (
      typeof response.ownerId === 'string' &&
      typeof response.vehicleDisplayName === 'string' &&
      !!response.performanceSafety &&
      !!response.fuelEfficiency &&
      Array.isArray(response.upcomingMaintenance)
    );
  }

  private clearLoadWatchdog(): void {
    if (this.loadWatchdogTimer !== null) {
      clearTimeout(this.loadWatchdogTimer);
      this.loadWatchdogTimer = null;
    }
  }

  private clearMileageReconciliation(): void {
    if (this.mileageReconcileTimer !== null) {
      clearTimeout(this.mileageReconcileTimer);
      this.mileageReconcileTimer = null;
    }
  }

  get maintenanceItems(): MaintenanceItemViewModel[] {
    return this.model?.upcomingMaintenance ?? [];
  }

  get maintenanceDisplayItems(): MaintenanceItemViewModel[] {
    return this.maintenanceItems.slice(0, 5);
  }

  displayMaintenanceName(itemName: string): string {
    const rule = this.getMaintenanceRule(itemName);
    if (rule) {
      return rule.label;
    }

    return itemName
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  maintenanceIcon(itemName: string): string {
    return this.getMaintenanceRule(itemName)?.icon ?? defaultMaintenanceIcon;
  }

  isCompactMaintenanceIcon(itemName: string): boolean {
    return this.getMaintenanceRule(itemName)?.useCompactIcon === true;
  }

  private getMaintenanceRule(itemName: string): MaintenanceDisplayRule | undefined {
    const normalized = itemName.trim().toLowerCase();
    return maintenanceDisplayRules.find((rule) =>
      rule.keywords.some((keyword) => normalized.includes(keyword))
    );
  }

  private getSavedOwnerId(): string | null {
    try {
      const stored = localStorage.getItem('autoInsight.ownerId');
      if (!stored) {
        return null;
      }

      const trimmed = stored.trim();
      if (trimmed.length === 0 || trimmed.toLowerCase() === 'name') {
        return null;
      }

      return trimmed;
    } catch {
      return null;
    }
  }

  private getSavedRole(): string | null {
    try {
      const stored = localStorage.getItem('autoInsight.role');
      if (!stored) {
        return null;
      }

      const trimmed = stored.trim().toLowerCase();
      return trimmed.length > 0 ? trimmed : null;
    } catch {
      return null;
    }
  }

  private getSavedUsername(): string | null {
    try {
      const stored = localStorage.getItem('autoInsight.username');
      if (!stored) {
        return null;
      }

      const trimmed = stored.trim();
      return trimmed.length > 0 ? trimmed : null;
    } catch {
      return null;
    }
  }

  private getSavedVehicleId(): number | undefined {
    try {
      const stored = localStorage.getItem('autoInsight.vehicleId');
      if (!stored) {
        return undefined;
      }

      const parsed = Number.parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private persistSelectedVehicleId(vehicleId: number): void {
    try {
      localStorage.setItem('autoInsight.vehicleId', vehicleId.toString());
    } catch {
      // Ignore storage limitations in private browser contexts.
    }
  }

  onManageVehicles(): void {
    const ownerId = this.model?.ownerId?.trim() || this.getSavedOwnerId();
    if (!ownerId) {
      return;
    }

    const queryParams: Record<string, string | number> = { ownerId };
    const selectedVehicleId = this.model?.vehicleId ?? this.getSavedVehicleId();
    if (selectedVehicleId !== undefined) {
      queryParams['selectedVehicleId'] = selectedVehicleId;
    }

    this.router.navigate(['/vehicle-profiles'], { queryParams });
  }

  onOpenMaintenanceReservations(): void {
    const queryParams: Record<string, string> = {};
    if (this.mechanicName) {
      queryParams['mechanic'] = this.mechanicName;
    }

    this.router.navigate(['/maintenance-reservations'], { queryParams });
  }

  onAddMileageToVehicle(): void {
    this.mileageErrorMessage = '';
    this.mileageSuccessMessage = '';

    if (this.isMechanicView) {
      this.mileageErrorMessage = 'Mileage updates are only available for owner dashboards.';
      return;
    }

    const ownerId = this.resolveOwnerId();
    if (!ownerId) {
      this.mileageErrorMessage = 'Owner account is required.';
      return;
    }

    const vehicleId = this.model?.vehicleId ?? this.getSavedVehicleId();
    if (vehicleId === undefined) {
      this.mileageErrorMessage = 'Select a vehicle before updating mileage.';
      return;
    }

    const mileageToAdd = this.parseMileageInput(this.mileageInput);
    if (mileageToAdd === null) {
      this.mileageErrorMessage = 'Enter a valid number of kilometers to add.';
      return;
    }

    const currentMileage = this.model?.currentOdometerKm;
    if (currentMileage === undefined || currentMileage === null || currentMileage < 0) {
      this.mileageErrorMessage = 'Current mileage is unavailable. Reload the dashboard and try again.';
      return;
    }

    const nextMileage = currentMileage + mileageToAdd;

    this.clearMileageReconciliation();
    this.mileageSaving = true;
    this.mileageRefreshing = false;
    this.mileageSuccessMessage = 'Saving mileage...';

    this.dashboardApi
      .updateVehicleMileage(ownerId, vehicleId, nextMileage)
      .pipe(
        timeout(this.mileageUpdateTimeoutMs),
        finalize(() => {
          this.mileageSaving = false;
        })
      )
      .subscribe({
        next: (vehicle) => {
          this.mileageInput = '';
          const confirmedMileage = this.resolveConfirmedMileage(vehicle, nextMileage);
          this.applyConfirmedMileageUpdate(ownerId, vehicleId, confirmedMileage);
        },
        error: (error: unknown) => {
          this.mileageRefreshing = true;
          this.mileageErrorMessage =
            error instanceof TimeoutError
              ? 'Mileage request timed out, still refreshing dashboard data...'
              : 'Unable to confirm the mileage update yet. Continuing dashboard refresh...';
          this.mileageSuccessMessage = 'Refreshing dashboard...';
          this.beginMileageReconciliation(ownerId, vehicleId, nextMileage);
        }
      });
  }

  goToMaintenanceParts(): void {
    this.router.navigate(['/maintenance-parts'], {
      queryParams: this.currentDashboardQueryParams()
    });
  }

  openMaintenancePart(item: MaintenanceItemViewModel): void {
    const normalizedPartName = this.displayMaintenanceName(item.name);
    this.router.navigate(['/schedule-maintenance'], {
      queryParams: {
        part: normalizedPartName,
        ...this.currentDashboardQueryParams()
      }
    });
  }

  private seedDashboardFromProfile(
    ownerId: string,
    vehicleId: number | undefined,
    requestKey: string,
    cachedModel: DashboardViewModel | null,
    forceApply: boolean,
    loadSequence: number,
    refreshToken?: number
  ): void {
    // Lightweight owner/profile seed to avoid blocking UI on slower aggregated dashboard calls.
    this.vehicleProfilesApi
      .getByOwner(ownerId, refreshToken)
      .pipe(
        timeout(this.seedRequestTimeoutMs),
        catchError(() => of([] as VehicleProfile[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((vehicles) => {
        if (this.activeRequestKey !== requestKey || this.loadSequence !== loadSequence) {
          return;
        }

        if (!forceApply && this.model && !this.isDashboardShell(this.model)) {
          return;
        }

        const seededModel = this.createSeededDashboard(ownerId, vehicleId, vehicles, [], cachedModel);
        if (!seededModel) {
          return;
        }

        this.model = seededModel;
        this.syncMileageInput(seededModel);
      });

    this.maintenanceSchedulingService
      .getDashboardSeedParts(ownerId, vehicleId, refreshToken)
      .pipe(
        timeout(this.seedRequestTimeoutMs),
        catchError(() => of([] as MaintenancePart[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((parts) => {
        if (this.activeRequestKey !== requestKey || this.loadSequence !== loadSequence || parts.length === 0) {
          return;
        }

        const currentModel =
          this.model && this.createRequestKey(this.model.ownerId, this.model.vehicleId) === requestKey
            ? this.model
            : cachedModel;

        if (!currentModel) {
          return;
        }

        const nextMaintenanceItems = this.buildDashboardMaintenanceItems(parts, currentModel.upcomingMaintenance);
        const updatedModel: DashboardViewModel = {
          ...currentModel,
          upcomingMaintenance: nextMaintenanceItems
        };

        this.model = updatedModel;
        this.syncMileageInput(updatedModel);
        this.persistDashboardCache(updatedModel);
      });
  }

  private beginMileageReconciliation(ownerId: string, vehicleId: number, expectedMileage: number, attempt = 0): void {
    this.mileageRefreshing = true;
    this.clearMileageReconciliation();

    // Poll profile mileage until write persistence is visible for the selected vehicle.
    this.vehicleProfilesApi
      .getByOwner(ownerId, this.createRefreshToken())
      .pipe(
        timeout(this.seedRequestTimeoutMs),
        catchError(() => of([] as VehicleProfile[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((vehicles) => {
        const confirmedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId) ?? null;
        const confirmedMileage = confirmedVehicle?.currentOdometerKm;

        if (confirmedMileage !== undefined && confirmedMileage >= expectedMileage) {
          this.applyConfirmedMileageUpdate(ownerId, vehicleId, confirmedMileage);
          return;
        }

        if (attempt + 1 >= this.mileageReconcileMaxAttempts) {
          this.mileageRefreshing = false;
          if (!this.mileageErrorMessage) {
            this.mileageErrorMessage = 'Mileage update is still processing. Reload the dashboard if values do not update.';
          }
          this.loadDashboard(ownerId, vehicleId, true, this.createRefreshToken());
          return;
        }

        this.mileageReconcileTimer = setTimeout(() => {
          this.beginMileageReconciliation(ownerId, vehicleId, expectedMileage, attempt + 1);
        }, this.mileageReconcileDelayMs);
      });
  }

  private resolveConfirmedMileage(vehicle: VehicleProfile | null | undefined, fallbackMileage: number): number {
    const confirmedMileage = vehicle?.currentOdometerKm;
    if (confirmedMileage !== undefined && confirmedMileage !== null && confirmedMileage >= 0) {
      return confirmedMileage;
    }

    return fallbackMileage;
  }

  private applyConfirmedMileageUpdate(ownerId: string, vehicleId: number, confirmedMileage: number): void {
    this.clearMileageReconciliation();
    this.mileageRefreshing = true;
    this.mileageErrorMessage = '';
    this.mileageSuccessMessage = 'Mileage saved. Refreshing dashboard...';
    // Apply the confirmed odometer and fast maintenance refresh immediately,
    // then let full dashboard hydration reconcile any remaining fields.
    this.syncConfirmedMileage(ownerId, vehicleId, confirmedMileage);
    this.refreshMaintenanceCardsAfterMileage(ownerId, vehicleId, confirmedMileage);
    this.loadDashboard(ownerId, vehicleId, true, this.createRefreshToken());
  }

  private syncConfirmedMileage(ownerId: string, vehicleId: number, confirmedMileage: number): void {
    const requestKey = this.createRequestKey(ownerId, vehicleId);
    const currentModel =
      this.model && this.createRequestKey(this.model.ownerId, this.model.vehicleId) === requestKey
        ? this.model
        : this.readCachedDashboard(ownerId, vehicleId);

    if (!currentModel) {
      return;
    }

    const updatedModel: DashboardViewModel = {
      ...currentModel,
      currentOdometerKm: confirmedMileage
    };

    this.model = updatedModel;
    this.persistDashboardCache(updatedModel);
  }

  private refreshMaintenanceCardsAfterMileage(
    ownerId: string,
    vehicleId: number,
    confirmedMileage: number,
    attempt = 0
  ): void {
    const requestKey = this.createRequestKey(ownerId, vehicleId);

    this.maintenanceSchedulingService
      .getDashboardSeedParts(ownerId, vehicleId, this.createRefreshToken())
      .pipe(
        timeout(this.seedRequestTimeoutMs),
        catchError(() => of([] as MaintenancePart[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((parts) => {
        if (this.activeRequestKey !== requestKey) {
          return;
        }

        const currentModel =
          this.model && this.createRequestKey(this.model.ownerId, this.model.vehicleId) === requestKey
            ? this.model
            : this.readCachedDashboard(ownerId, vehicleId);

        if (!currentModel) {
          this.mileageRefreshing = false;
          this.mileageSuccessMessage = 'Mileage added.';
          return;
        }

        const updatedModel: DashboardViewModel = {
          ...currentModel,
          currentOdometerKm: confirmedMileage,
          upcomingMaintenance:
            parts.length > 0
              ? this.buildDashboardMaintenanceItems(parts, currentModel.upcomingMaintenance)
              : currentModel.upcomingMaintenance
        };

        this.model = updatedModel;
        this.persistDashboardCache(updatedModel);

        if (parts.length === 0 && attempt + 1 < this.mileageRefreshRetryMaxAttempts) {
          // Brief retry window to tolerate post-write lag before showing stale cards.
          this.mileageReconcileTimer = setTimeout(() => {
            this.refreshMaintenanceCardsAfterMileage(ownerId, vehicleId, confirmedMileage, attempt + 1);
          }, this.mileageReconcileDelayMs);
          return;
        }

        this.mileageRefreshing = false;
        this.mileageSuccessMessage = 'Mileage added.';
      });
  }

  private buildDashboardMaintenanceItems(
    parts: MaintenancePart[],
    fallbackItems: MaintenanceItemViewModel[] = []
  ): MaintenanceItemViewModel[] {
    const fallbackByName = new Map(
      fallbackItems.map((item) => [this.normalizeMaintenanceName(item.name), item] as const)
    );

    return dashboardMaintenancePartNames
      .map((partName) => {
        const normalizedPartName = this.normalizeMaintenanceName(partName);
        const matchedPart =
          parts.find((part) => this.normalizeMaintenanceName(part.name) === normalizedPartName) ?? null;

        if (matchedPart) {
          return {
            name: partName,
            remainingLifePercent: this.clampPercent(matchedPart.durabilityPercent)
          };
        }

        return fallbackByName.get(normalizedPartName) ?? null;
      })
      .filter((item): item is MaintenanceItemViewModel => item !== null);
  }

  private createSeededDashboard(
    ownerId: string,
    vehicleId: number | undefined,
    vehicles: VehicleProfile[],
    parts: MaintenancePart[],
    cachedModel: DashboardViewModel | null
  ): DashboardViewModel | null {
    const selectedVehicle =
      vehicles.find((vehicle) => (vehicleId !== undefined ? vehicle.id === vehicleId : true)) ?? vehicles[0] ?? null;
    const cacheForSelection =
      cachedModel && this.createRequestKey(cachedModel.ownerId, cachedModel.vehicleId) === this.createRequestKey(ownerId, vehicleId)
        ? cachedModel
        : null;

    if (!selectedVehicle && !cacheForSelection) {
      return null;
    }

    const currentVehicleId = selectedVehicle?.id ?? vehicleId ?? cacheForSelection?.vehicleId;
    const currentOdometerKm = selectedVehicle?.currentOdometerKm ?? cacheForSelection?.currentOdometerKm;
    const seededMaintenance = this.buildDashboardMaintenanceItems(parts, cacheForSelection?.upcomingMaintenance ?? []);

    return {
      ownerId,
      vehicleId: currentVehicleId,
      currentOdometerKm,
      vehicleDisplayName:
        selectedVehicle
          ? `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}`
          : (cacheForSelection?.vehicleDisplayName ?? 'Selected vehicle'),
      emptyStateMessage: undefined,
      performanceSafety: {
        tireTreadPercent: cacheForSelection?.performanceSafety.tireTreadPercent ?? 0,
        accelerationZeroToHundredSeconds:
          cacheForSelection?.performanceSafety.accelerationZeroToHundredSeconds ?? 0,
        topSpeedKmh: cacheForSelection?.performanceSafety.topSpeedKmh ?? 0
      },
      fuelEfficiency: {
        cityKmPerL: cacheForSelection?.fuelEfficiency.cityKmPerL ?? 0,
        highwayKmPerL: cacheForSelection?.fuelEfficiency.highwayKmPerL ?? 0
      },
      upcomingMaintenanceDate: cacheForSelection?.upcomingMaintenanceDate ?? '',
      upcomingMaintenance: seededMaintenance
    };
  }

  private createDashboardShell(ownerId: string, vehicleId?: number): DashboardViewModel {
    return {
      ownerId,
      vehicleId,
      currentOdometerKm: undefined,
      vehicleDisplayName: 'Selected vehicle',
      emptyStateMessage: undefined,
      performanceSafety: {
        tireTreadPercent: 0,
        accelerationZeroToHundredSeconds: 0,
        topSpeedKmh: 0
      },
      fuelEfficiency: {
        cityKmPerL: 0,
        highwayKmPerL: 0
      },
      upcomingMaintenanceDate: '',
      upcomingMaintenance: []
    };
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round(value)));
  }

  private normalizeMaintenanceName(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private createRefreshToken(): number {
    return Date.now();
  }

  private createRequestKey(ownerId: string, vehicleId?: number): string {
    return `${ownerId.trim().toLowerCase()}::${vehicleId ?? 'latest'}`;
  }

  private dashboardCacheKey(ownerId: string, vehicleId?: number): string {
    return `autoInsight.dashboard.v2.${this.createRequestKey(ownerId, vehicleId)}`;
  }

  private readCachedDashboard(ownerId: string, vehicleId?: number): DashboardViewModel | null {
    try {
      const raw = localStorage.getItem(this.dashboardCacheKey(ownerId, vehicleId));
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as DashboardViewModel | null;
      if (
        parsed &&
        (parsed.ownerId?.trim().toLowerCase() === 'name' ||
          parsed.vehicleDisplayName?.trim().toLowerCase() === '2012 honda civic' ||
          this.hasLikelySyntheticMaintenanceCache(parsed))
      ) {
        return null;
      }

      return this.isDashboardResponseValid(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private readCachedDashboardByOwner(ownerId: string, preferredVehicleId?: number): DashboardViewModel | null {
    const normalizedOwner = ownerId.trim().toLowerCase();
    if (!normalizedOwner) {
      return null;
    }

    try {
      const prefix = `autoInsight.dashboard.v2.${normalizedOwner}::`;
      const matchingKeys: string[] = [];
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);
        if (!key || !key.startsWith(prefix)) {
          continue;
        }

        matchingKeys.push(key);
      }

      if (matchingKeys.length === 0) {
        return null;
      }

      for (const key of matchingKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        const parsed = JSON.parse(raw) as DashboardViewModel | null;
        if (!this.isDashboardResponseValid(parsed) || this.hasLikelySyntheticMaintenanceCache(parsed)) {
          continue;
        }

        if (preferredVehicleId !== undefined && parsed.vehicleId === preferredVehicleId) {
          return parsed;
        }
      }

      // When a specific vehicle is selected, do not fall back to another
      // vehicle's cached dashboard because that can show mismatched mileage.
      if (preferredVehicleId !== undefined) {
        return null;
      }

      for (const key of matchingKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        const parsed = JSON.parse(raw) as DashboardViewModel | null;
        if (this.isDashboardResponseValid(parsed) && !this.hasLikelySyntheticMaintenanceCache(parsed)) {
          return parsed;
        }
      }
    } catch {
      return null;
    }

    return null;
  }

  private persistDashboardCache(model: DashboardViewModel): void {
    if (!this.isDashboardResponseValid(model)) {
      return;
    }

    try {
      localStorage.setItem(this.dashboardCacheKey(model.ownerId, model.vehicleId), JSON.stringify(model));
    } catch {
      // Ignore storage quota/private mode limitations.
    }
  }

  private hasLikelySyntheticMaintenanceCache(model: DashboardViewModel): boolean {
    const currentOdometerKm = model.currentOdometerKm ?? 0;
    if (currentOdometerKm <= 0 || model.upcomingMaintenance.length === 0) {
      return false;
    }

    return model.upcomingMaintenance.every((item) => item.remainingLifePercent === 100);
  }

  private isDashboardShell(model: DashboardViewModel | null): boolean {
    if (!model) {
      return false;
    }

    return (
      model.vehicleDisplayName === 'Selected vehicle' &&
      (model.currentOdometerKm === undefined || model.currentOdometerKm === null) &&
      model.upcomingMaintenance.length === 0 &&
      model.performanceSafety.tireTreadPercent === 0 &&
      model.performanceSafety.topSpeedKmh === 0 &&
      model.fuelEfficiency.cityKmPerL === 0 &&
      model.fuelEfficiency.highwayKmPerL === 0
    );
  }

  private parseMileageInput(rawValue: string): number | null {
    const trimmed = rawValue.trim();
    if (!/^\d+$/.test(trimmed)) {
      return null;
    }

    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  }

  private syncMileageInput(model: DashboardViewModel): void {
    this.mileageInput = '';
  }

  private persistOwnerId(ownerId: string): void {
    if (!ownerId || ownerId.trim().toLowerCase() === 'name') {
      return;
    }

    try {
      localStorage.setItem('autoInsight.ownerId', ownerId.trim());
    } catch {
      // Ignore storage failures in restricted contexts.
    }
  }

  private clearLegacyDashboardCache(): void {
    try {
      for (let index = localStorage.length - 1; index >= 0; index--) {
        const key = localStorage.key(index);
        if (!key) {
          continue;
        }

        if (key.startsWith('autoInsight.dashboard.') && !key.startsWith('autoInsight.dashboard.v2.')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Ignore storage failures in restricted contexts.
    }
  }

  private resolveOwnerId(): string {
    const modelOwner = this.model?.ownerId?.trim() ?? '';
    if (!modelOwner || modelOwner.toLowerCase() === 'name') {
      const saved = this.getSavedOwnerId();
      return saved ?? '';
    }

    return modelOwner;
  }

  private currentDashboardQueryParams(): Record<string, string | number> {
    const queryParams: Record<string, string | number> = {};

    const ownerId = this.resolveOwnerId();
    if (ownerId) {
      queryParams['ownerId'] = ownerId;
    }

    const selectedVehicleId = this.model?.vehicleId ?? this.getSavedVehicleId();
    if (selectedVehicleId !== undefined) {
      queryParams['vehicleId'] = selectedVehicleId;
    }

    return queryParams;
  }
}
